import {
    h, ComponentChildren, toChildArray, VNode, ComponentChild, cloneElement, RefObject, isValidElement, Fragment
} from "preact";
import { useEffect, useRef, useState } from "preact/hooks";

import { Keyboard } from "../../../foundation/enums";
import BasicRecord from "../../../foundation/utils/BasicRecord";
import classname from "../../../foundation/utils/classname";
import { modifierKeysUsed } from "../../../foundation/utils/keyboard";
import { scrollIfNecessary } from "../../../foundation/utils/platform";
import { usePrevious } from "../../hooks";

import NavigationItem, { NavigationItemOwnProps } from "./NavigationItem";
import Action from "./NavigationListAction";

import styles from "./NavigationList.module.css";

interface Props<T> {
    class?: string;
    initialIndex?: number;
    preventSpaceAction?: boolean;
    children: ComponentChildren;
    mapEventToAction?: (event: KeyboardEvent) => Action | undefined;
    onNavigatedItemChange?: (item: T | undefined) => void;
}

function isNavigable<T>(child: ComponentChild): child is VNode<NavigationItemOwnProps<T>> {
    return isNavigationItem(child) && !child.props.disabled;
}

function isNavigationItem(child: ComponentChild): child is VNode<NavigationItemOwnProps> {
    if (!child) {
        return false;
    }

    const { type } = child as VNode;

    return !!type && typeof type !== "string" && type.name === NavigationItem.name;
}

function getNavigableChildren<T>(
    children: ComponentChildren,
    navigableChildren: VNode<NavigationItemOwnProps<T>>[] = []
): VNode<NavigationItemOwnProps<T>>[] {
    toChildArray(children).forEach(child => {
        if (isNavigable<T>(child)) {
            navigableChildren.push(child);
        } else if (typeof child === "object" && child.props.children) {
            return getNavigableChildren(child.props.children, navigableChildren);
        }
    });

    return navigableChildren;
}

function NavigationList<T = unknown>({
    class: cssClass,
    initialIndex,
    children,
    preventSpaceAction,
    mapEventToAction,
    onNavigatedItemChange
}: Props<T>) {
    const [mouseActive, setMouseActive] = useState(initialIndex !== undefined);
    const navigableChildren = getNavigableChildren<T>(children);
    const previousNavigableChildrenLength = usePrevious(navigableChildren.length);
    const [navigatedIndex, setNavigatedIndex] = useState(initialIndex);

    const itemRefs: BasicRecord<RefObject<HTMLDivElement>> = {};

    function getItemRef(index: number): RefObject<HTMLDivElement> {
        const key = `navigationItem-${index}`;
        itemRefs[key] ??= useRef();
        return itemRefs[key];
    }

    function handleItemHover(ref: RefObject<HTMLDivElement>) {
        const index = Object.keys(itemRefs).findIndex(key => itemRefs[key] === ref);
        setNavigatedIndex(index);
    }

    function handleItemMouseLeave() {
        if (mouseActive) {
            setNavigatedIndex(undefined);
        }
    }

    function handleMouseMove() {
        setMouseActive(true);
    }

    useEffect(() => {
        function getAction(event: KeyboardEvent): Action {
            const mappedAction = mapEventToAction?.(event);
            if (mappedAction) {
                return mappedAction;
            }

            if (event.key === Keyboard.Key.ArrowUp) {
                return Action.Up;
            } else if (event.key === Keyboard.Key.ArrowDown) {
                return Action.Down;
            } else if (event.key === Keyboard.Key.Enter) {
                return Action.Click;
            } else if (event.code === Keyboard.Code.Space && !preventSpaceAction) {
                return Action.Click;
            }

            return Action.None;
        }

        function handleKeyDown(event: KeyboardEvent) {
            if (!navigableChildren.length || modifierKeysUsed(event)) {
                return;
            }

            let newIndex;

            switch (getAction(event)) {
                case Action.Up:
                    setMouseActive(false);
                    event.stopPropagation();
                    event.preventDefault();
                    newIndex = navigatedIndex === undefined
                        ? 0
                        : Math.max(navigatedIndex - 1, 0);
                    setNavigatedIndex(newIndex);
                    break;
                case Action.Down:
                    setMouseActive(false);
                    event.stopPropagation();
                    event.preventDefault();
                    newIndex = navigatedIndex === undefined
                        ? 0
                        : Math.min(navigatedIndex + 1, navigableChildren.length - 1);
                    setNavigatedIndex(newIndex);
                    break;
                case Action.Click:
                    event.stopPropagation();
                    event.preventDefault();
                    if (navigatedIndex !== undefined) {
                        getItemRef(navigatedIndex).current!.click();
                    }

                    break;
                default:
                    break;
            }
        }

        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [navigatedIndex, navigableChildren.length, mapEventToAction]);

    useEffect(() => {
        if (navigatedIndex !== undefined) {
            scrollIfNecessary(getItemRef(navigatedIndex).current);
        }
    }, [navigatedIndex]);

    useEffect(() => {
        if (previousNavigableChildrenLength && navigableChildren.length !== previousNavigableChildrenLength) {
            setNavigatedIndex(initialIndex);
        }
    }, [children]);

    useEffect(() => {
        const item = navigatedIndex === undefined
            ? undefined
            : navigableChildren[navigatedIndex].props.item;
        onNavigatedItemChange?.(item);
    }, [navigatedIndex]);

    let navigableIndex = -1;

    function cloneChildren(componentChildren: ComponentChildren): ComponentChild[] {
        return toChildArray(componentChildren).map(child => {
            if (isNavigable(child)) {
                const navigated = navigatedIndex !== undefined && child === navigableChildren[navigatedIndex];
                // As navigable items are mapped into refs in the same order they are rendered, just incrementing
                // `navigableIndex` when a navigable item is found is enough to find its index, otherwise we should
                // have used a findIndex method on `navigableChildren` with `child` which has O(n) complexity.
                navigableIndex++;

                return cloneElement(child, {
                    baseRef: getItemRef(navigableIndex),
                    navigated,
                    onHover: handleItemHover,
                    onMouseLeave: handleItemMouseLeave
                });
            }

            if (isValidElement(child) && child.props.children) {
                return cloneElement(child, {
                    children: cloneChildren(child.props.children)
                });
            }

            return child;
        });
    }

    return (
        <div
            class={classname(styles.navigationList, cssClass)}
            onMouseMove={handleMouseMove}>
            {cloneChildren(children)}
        </div>
    );
}

export default NavigationList;
export {
    Props as NavigationListProps
};
